import React, { FC, useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TablePagination,
  Grid,
} from '@material-ui/core';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Alert from '@material-ui/lab/Alert';
import { Longdash } from '~/utils';
import { TextInput, CurrencyAutocomplete, SelectInput, ChipField } from '~/components';
import { useTranslate } from 'react-admin';
import { FilterType, TableProps } from '~/types/AccountTableMultiselect';
import TableContent from './TableContent';
import { useGetAllAccountsWithHeaders } from '~/hooks/accounts';
import { Account } from '~/types';
import { useHandbooksQuery } from '~/hooks/useHandbook';
import debounce from 'lodash/debounce';
import { ACCOUNTS_PRODUCT_CATEGORIES } from '~/utils/constants/accounts';

const useStyles = makeStyles(() => ({
  pagination: {
    '& > div': {
      backgroundColor: 'transparent',
      borderTop: 'none',
    },
  },
  warning: {
    marginBottom: 16,
  },
}));

const getTotalCountFromContentRange = (headers: any): number => {
  const contentRange = headers?.['content-range'];
  if (!contentRange) return 0;
  const total = contentRange.split('/')[1];
  return parseInt(total, 10);
};

export interface AccountsTableDialogProps {
  resource: 'businesses' | 'individuals' | 'partners' | 'tenant/accounts';
  profileId?: string;
  solutionId?: string;
  open: boolean;
  onClose: () => void;
  title?: string;
  warning?: string;
  filters?: FilterType[];
  tableProps?: TableProps;
  optionText: string | ((x: Account) => string);
  children?: (item: Account, index: number, arr: Account[]) => any;
  isSelected: (id: string) => boolean;
  handleToggleSelect: (item: Account) => void;
  hasSelection?: boolean;
  handleDeselectAll: () => void;
  renderActions: () => React.ReactNode;
  additionalFilters?: { [key: string]: any };
  selectionControl?: 'checkbox' | 'radio';
  selectedItems?: Account[];
  handleDeselect?: (id: string) => void;
  chipVariant?: 'currency' | 'balance';
}

const AccountsTableDialog: FC<AccountsTableDialogProps> = ({
  resource,
  profileId,
  solutionId,
  open,
  onClose,
  title,
  warning,
  filters,
  tableProps,
  optionText,
  children,
  isSelected,
  handleToggleSelect,
  hasSelection,
  handleDeselectAll,
  renderActions,
  additionalFilters,
  selectionControl = 'checkbox',
  selectedItems,
  handleDeselect,
  chipVariant = 'currency',
}) => {
  const classes = useStyles();
  const translate = useTranslate();
  const t = (key: string): string =>
    translate(`components.ra.fields.AccountTableMultiselect.${key}`);
  const tt = (key: string): string => translate(`resources.businesses.terminals.${key}`);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allFilters, setAllFilters] = useState<{ [key in FilterType]?: any }>({});
  const [debouncedFilters, setDebouncedFilters] = useState<{ [key in FilterType]?: any }>({});
  const [totalCount, setTotalCount] = useState(0);

  const debouncedFilterEffect = React.useRef(
    debounce((f) => {
      setDebouncedFilters(f);
    }, 500)
  ).current;

  useEffect(() => {
    debouncedFilterEffect(allFilters);
  }, [allFilters, debouncedFilterEffect]);

  // Reset state when dialog closes
  useEffect(() => {
    if (!open) {
      setPage(0);
      setRowsPerPage(10);
      setAllFilters({});
    }
  }, [open]);

  const { data: paginatedAccounts, isLoading } = useGetAllAccountsWithHeaders(
    {
      resource,
      profileId,
      pagination: { page: page + 1, perPage: rowsPerPage },
      filters: {
        ...debouncedFilters,
        ...additionalFilters,
        ...(resource !== 'tenant/accounts'
          ? { excludedProductCategories: ACCOUNTS_PRODUCT_CATEGORIES }
          : {}),
      },
    },
    {
      enabled: open && (!!profileId || resource === 'tenant/accounts'),
      onSuccess: (data) => {
        setTotalCount(getTotalCountFromContentRange(data?.headers));
      },
    }
  );

  const { data: handbook } = useHandbooksQuery(solutionId);
  const currencies = handbook?.currencies;
  const rows = paginatedAccounts?.data || [];

  const handleFilterChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPage(0);
    const { name, value } = event.target;
    setAllFilters((prev) => ({ ...prev, [name]: value ? value : undefined }));
  };

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    if (!event.target) return;
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const findFilter = (key: FilterType) => {
    switch (key) {
      case 'status':
        return (
          <SelectInput
            label={t('status._')}
            options={[
              { value: 'ACTIVE', text: t('status.ACTIVE') },
              { value: 'BLOCKED', text: t('status.BLOCKED') },
              { value: 'CLOSED', text: t('status.CLOSED') },
            ]}
            allowEmpty
            emptyText={Longdash}
            onChange={handleFilterChange}
            value={allFilters.status}
            name='status'
            disableHelperText
          />
        );
      case 'number':
        return (
          <TextInput
            label={t('accountNumber')}
            onChange={handleFilterChange}
            value={allFilters.number}
            name='number'
            disableHelperText
          />
        );
      case 'alias':
        return (
          <TextInput
            label={t('alias')}
            value={allFilters.alias}
            onChange={handleFilterChange}
            name='alias'
            disableHelperText
          />
        );
      case 'currency': {
        const lockedCurrency = additionalFilters?.currency;
        return (
          <CurrencyAutocomplete
            label={t('currency')}
            onChange={handleFilterChange}
            value={lockedCurrency ?? allFilters.currency}
            name='currency'
            disabled={!!lockedCurrency}
            filterOptions={(all: string[]) =>
              currencies ? all.filter((c) => currencies.find((hc) => hc.id === c)) : all
            }
            disableHelperText
          />
        );
      }
      default:
        return null;
    }
  };

  const renderFilters = (filterKeys: FilterType[]) => (
    <Grid container spacing={1} style={{ marginBottom: 16 }}>
      {filterKeys.map((filter, index) => (
        <Grid item xs={12} sm={6} md={3} key={index}>
          {findFilter(filter)}
        </Grid>
      ))}
    </Grid>
  );

  return (
    <Dialog onClose={onClose} aria-labelledby='accounts-dialog-title' open={open} maxWidth='xl'>
      <DialogTitle id='accounts-dialog-title'>{title}</DialogTitle>
      <DialogContent style={{ minHeight: 570, width: 786 }}>
        {warning && (
          <Alert className={classes.warning} severity='warning'>
            {warning}
          </Alert>
        )}
        {filters && renderFilters(filters)}
        {selectedItems && selectedItems.length > 0 && (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 12 }}>
            {selectedItems.map((account) => {
              const number = account.number;
              const alias = account.alias;
              const name = `${number}${alias ? ` (${alias})` : ''}`;
              const suffix =
                chipVariant === 'balance'
                  ? account.balances
                      ?.find((b) => b.code === 'CURRENT_AVAILABLE' || b.code === 'CURRENT_FLOAT')
                      ?.amount.value.toLocaleString('en-US', {
                        style: 'currency',
                        currency: account.currency,
                      }) ?? account.currency
                  : account.currency;
              return (
                <ChipField
                  key={account.id}
                  label={`${name} · ${suffix}`}
                  onDelete={handleDeselect ? () => handleDeselect(account.id) : undefined}
                />
              );
            })}
          </div>
        )}
        {isLoading ? (
          <div>{tt('searchInProgress')}</div>
        ) : (
          <>
            <TableContent
              rows={rows}
              page={page}
              rowsPerPage={rowsPerPage}
              isSelected={isSelected}
              handleToggleSelect={handleToggleSelect}
              optionText={optionText}
              children={children}
              tableProps={tableProps}
              hasSelection={hasSelection}
              handleDeselectAll={handleDeselectAll}
              selectionControl={selectionControl}
            />
            <TablePagination
              rowsPerPageOptions={[10, 20, 50]}
              rowsPerPage={rowsPerPage}
              component='div'
              count={totalCount}
              className={classes.pagination}
              page={page}
              onChangePage={handleChangePage}
              onChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>{renderActions()}</DialogActions>
    </Dialog>
  );
};

export default AccountsTableDialog;
